import React, {useEffect, useMemo, useState} from 'react';
import {Button} from 'primereact/button';
import {Message} from 'primereact/message';
import {MultiSelect} from 'primereact/multiselect';

import {Loading} from '../../utils/InlineComponents';
import {isAdmin} from '../../utils/Utils';
import {MarketplaceProductService} from '../../service/MarketplaceProductService';
import {ScraperService} from '../../service/ScraperService';
import ScrapeStatus from '../Product/ScrapeStatus';
import {MARKETPLACES} from './MarketplaceConnectStep';
import {MarketplaceBadge} from './MarketplaceIntegrationStep';
import MarketplaceProductDialog from '../Campaign/MarketplaceProductDialog';

const scraperService = new ScraperService();

// Admin-only "Update Product Fields": re-scrape the marketplace product page and
// fill catalog fields the API can't (Hepsiburada's Listing API omits name/image,
// and its catalog API doesn't cover "matched" products). Field-only scrape
// (products_update_partial) -- prices are untouched. Excludes ref_id (the hbSku
// tracking key must never be overwritten) and reviews (a separate flow). Works
// for any buybox campaign; the scrape routes each product URL to the right
// per-host spider (Trendyol products already come full via API, so it's mainly
// useful for Hepsiburada).
const PARTIAL_FIELDS = [
    {name: 'Name', code: 'name'}, {name: 'Image', code: 'image'}, {name: 'Brand', code: 'brand'},
    {name: 'Barcode', code: 'barcode'}, {name: 'Ean', code: 'ean'}, {name: 'Sku', code: 'sku'},
    {name: 'Mpn', code: 'mpn'}, {name: 'Upc', code: 'upc'}, {name: 'Other Codes', code: 'other_codes'},
    {name: 'Description', code: 'description'},
];
const DEFAULT_PARTIAL_CODES = ['name', 'image', 'brand', 'barcode', 'ean', 'sku', 'other_codes'];

// Onboarding step for mp_products (Task 18). Mirrors ProductsOnboardingStep's
// Shopify branch: a green "here's what's connected" summary panel + a button
// that opens a dialog picker (MarketplaceProductDialog), instead of the
// always-open inline search table this step used to render directly (moved
// into the dialog, see that file).
//
// Tracked-count source: getIntegration() (used by the sibling "integration"
// step) does not include a product count, and the onboarding `status` prop
// available to steps like ProductsOnboardingStep isn't wired to this step
// (OnboardingStepContent only passes t/campaignId here) -- and even if it
// were, the backend's OnboardingService doesn't compute a per-step summary
// for the "marketplace_products" key today, so it would read as 0 even when
// products are tracked. Counting the first page of listProducts() would
// also be wrong across pages. Instead this reuses
// MarketplaceProductService.getTrackedCount(), which hits the SAME
// count_active_and_inactive_products endpoint the campaign list cards use
// for "Product Count" -- a real Product-collection count that already
// includes marketplace-tracked products (tracking upserts real Product
// documents; see service_marketplace_campaign.py _upsert_owner_product).
const MarketplaceProductsStep = ({t, campaignId}) => {
    const service = useMemo(() => new MarketplaceProductService(), []);
    const [integration, setIntegration] = useState(null);
    const [trackedCount, setTrackedCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [showDialog, setShowDialog] = useState(false);
    const admin = isAdmin();
    const [partialFields, setPartialFields] = useState(
        PARTIAL_FIELDS.filter(f => DEFAULT_PARTIAL_CODES.includes(f.code)));
    const [partialStatus, setPartialStatus] = useState(null);

    const loadIntegration = () => service.getIntegration(campaignId).then(setIntegration).catch(() => setIntegration(null));
    const loadCount = () => service.getTrackedCount(campaignId).then(setTrackedCount).catch(() => setTrackedCount(0));
    const loadPartialStatus = () => scraperService.getLastScrapingRequest(campaignId, 'products_update_partial')
        .then(reqs => setPartialStatus(reqs && reqs[0])).catch(() => {});

    // Both fetches share one `loading` gate (avoids a flash of "not
    // connected" or "0 tracked" before either resolves); a later
    // count-only refresh (onChanged below) does not touch `loading`, so the
    // panel doesn't flicker after closing the dialog.
    useEffect(() => {
        if (!campaignId) return;
        setLoading(true);
        Promise.all([loadIntegration(), loadCount()]).finally(() => setLoading(false));
        if (admin) loadPartialStatus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [campaignId]);

    const marketplaceKey = integration?.marketplace;
    const marketplaceMeta = MARKETPLACES.find(m => m.key === marketplaceKey);
    const credentials = (marketplaceKey && integration?.credentials?.[marketplaceKey]) || null;
    const merchantIdentity = (credentials && (credentials.merchant_name || credentials.merchant_id)) || '';

    // No marketplace connected yet -> no brand name to interpolate; fall
    // back to the generic "Marketplace" / "Pazaryeri" label already used by
    // the connect step's picker.
    const marketplaceLabel = marketplaceMeta ? marketplaceMeta.label : t('wizard.mpConnect.marketplacePicker.label');
    const manageLabel = t('onboarding.products.marketplace.manage', 'Choose products from your {marketplace} store')
        .replace('{marketplace}', marketplaceLabel);

    return (
        <div>
            {loading
                ? <div style={{textAlign: 'center', padding: '2em 1em'}}>
                    <Loading/>
                    <div style={{marginTop: 12, color: '#6b7280'}}>{t('onboarding.loading', 'Loading…')}</div>
                  </div>
                : marketplaceMeta
                ? <div style={{border: '1px solid #c7e3d4', background: '#f0fdf4', borderRadius: 10, padding: 16, marginBottom: 16}}>
                    <MarketplaceBadge marketplace={marketplaceMeta}/>
                    <div style={{color: '#4b5563', marginTop: 10, fontSize: '0.9rem'}}>
                        {t('onboarding.products.marketplace.desc',
                            '{n} products tracked. Products are imported directly from {marketplace} via API. No scraping needed.')
                            .replace('{n}', trackedCount).replace('{marketplace}', marketplaceMeta.label)}
                        {merchantIdentity ? ` · ${merchantIdentity}` : ''}
                    </div>
                    <div style={{marginTop: 12}}>
                        <Button label={manageLabel} icon="pi pi-box" className="p-button-sm p-button-success"
                                onClick={() => setShowDialog(true)}/>
                    </div>
                  </div>
                : <div style={{marginBottom: 16}}>
                    <Message severity="info" text={t('onboarding.products.marketplace.notConnected',
                        'Connect your marketplace account on the Integration step first to manage products.')}/>
                    <div style={{marginTop: 12}}>
                        <Button label={manageLabel} icon="pi pi-box" className="p-button-sm p-button-secondary" disabled/>
                    </div>
                  </div>}

            {marketplaceMeta && admin &&
                <div style={{border: '1px solid #e5e7eb', borderRadius: 10, padding: 16, background: '#fff',
                             display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16, maxWidth: 480}}>
                    <div style={{fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8}}>
                        <i className="pi pi-sync" style={{color: '#2563eb'}}/>
                        {t('onboarding.products.scrapingUpdateFields', 'Update Product Fields')}
                    </div>
                    <small style={{color: '#6b7280'}}>{t('onboarding.products.scrapingUpdateFieldsDesc',
                        'Re-scrape and update the selected fields for all products.')}</small>
                    <MultiSelect value={partialFields} options={PARTIAL_FIELDS} optionLabel="name" display="chip" filter
                                 style={{width: '100%'}} placeholder={t('onboarding.products.scrapingFields', 'Alanlar')}
                                 onChange={(e) => setPartialFields(e.value || [])}/>
                    <ScrapeStatus type="product" text={t('onboarding.products.scrapingUpdateFields', 'Update Product Fields')}
                                  scraping_request={partialStatus} campaign_id={campaignId}
                                  product_id={null} competitor_url={null} newRequestCompleted={() => loadPartialStatus()}
                                  newRequest={{spider_name: 'products_update_partial', type: 'products_update_partial',
                                               fields: partialFields.map(f => f.code), campaign_id: campaignId}}/>
                </div>}

            {marketplaceMeta &&
                <MarketplaceProductDialog visible={showDialog} campaignId={campaignId}
                                          onHide={() => setShowDialog(false)}
                                          onChanged={() => loadCount()}/>}
        </div>
    );
};

export default MarketplaceProductsStep;
